import React from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter, Navigate, redirect } from 'react-router'
import { ApplicationRoutes } from '~/types/RoutingTypes'

import { getAuthStateFromLocalStorage } from './atoms/AuthenticationState'
import { ResourceErrorBoundary } from './components/ResourceErrorBoundary'
import { AdminPanel } from './pages/Admin/AdminPanel'
import { CallAnalyticsPage } from './pages/CallAnalytics/CallAnalyticsPage'
import { CallDetailsPage, CallsPage } from './pages/Calls/CallsPage'
import { ContactsPage } from './pages/Contacts/ContactsPage'
import { AgentStatusPage } from './pages/Dashboards/AgentStatusPage'
import { DashboardIndexRedirect } from './pages/Dashboards/DashboardIndexRedirect'
import { DashboardsLayout } from './pages/Dashboards/DashboardsLayout'
import { EventLogPage } from './pages/Dashboards/EventLogPage'
import { ExtensionComparisonPage } from './pages/Dashboards/ExtensionComparisonPage'
import { InboundAnswerRatePage } from './pages/Dashboards/InboundAnswerRatePage'
import { NumberComparisonPage } from './pages/Dashboards/NumberComparisonPage'
import { QueueDashboardPage } from './pages/Dashboards/QueueDashboardPage'
import { FaxPage } from './pages/Fax/FaxPage'
import { ForgotPasswordPage } from './pages/ForgotPassword/ForgotPasswordPage'
import { DefaultHomeRoute } from './pages/Home/DefaultHomeRoute'
import { HomePage } from './pages/Home/HomePage'
import {
  HotelsDashboardRoutePage,
  HotelsLayout,
  HotelsSettingsRoutePage,
  WakeupHistoryRoutePage,
  WakeupSchedulerRoutePage,
} from './pages/Hotels'
import { LoginPage, SelectPbxPage } from './pages/Login/LoginPage'
import MessagingPage from './pages/Messaging/MessagingPage'
import { MissedCallsPage } from './pages/MissedCalls/MissedCallsPage'
import { OpenAppPage } from './pages/OpenApp/OpenAppPage'
import { OpenedInDesktopPage } from './pages/OpenApp/OpenedInDesktopPage'
import { PopupPage } from './pages/Popup/PopupPage'
import { ResetPasswordPage } from './pages/ResetPassword/ResetPasswordPage'
import { SchedulesRedirect } from './pages/Schedules/SchedulesRedirect'
import { SecureFaxMockPage } from './pages/SecureFax/SecureFaxMockPage'
import { SecureFaxPage } from './pages/SecureFax/SecureFaxPage'
import { SettingsPage } from './pages/Settings/SettingsPage'
import { StatusPanel } from './pages/Status/StatusPanel'
import { VoicemailPage } from './pages/Voicemail/VoicemailPage'
import WebPhonePage from './pages/WebPhone/WebPhonePage'
import { getUserFromToken } from './utils/auth'
import { redirectToDesktopHandoffIfNeeded } from './utils/desktopAppHandoff'

const IconsPage = React.lazy(() =>
  import('./pages/Dev/Icons').then((module) => ({ default: module.IconsPage })),
)

// Create a loading component for Suspense fallback
const LoadingComponent = () => {
  const { t } = useTranslation()
  return <div>{t('router.loadingContent', 'Loading content...')}</div>
}

const HotelsDashboardRoute = () => (
  <HotelsLayout>
    <HotelsDashboardRoutePage />
  </HotelsLayout>
)

const HotelsSettingsRoute = () => (
  <HotelsLayout>
    <HotelsSettingsRoutePage />
  </HotelsLayout>
)

const HotelsWakeupRoute = () => (
  <HotelsLayout>
    <WakeupSchedulerRoutePage />
  </HotelsLayout>
)

const HotelsWakeupHistoryRoute = () => (
  <HotelsLayout>
    <WakeupHistoryRoutePage />
  </HotelsLayout>
)

// Auth check loader helper
const authCheckLoader = async ({ request }: { request: Request }) => {
  const authState = getAuthStateFromLocalStorage()
  const token = authState.appSessionJwts?.access_token || undefined

  const user = getUserFromToken(token)
  const isUserLoggedIn = !!user
  const userStateReady = !!user

  const shouldRedirectToLogin = !isUserLoggedIn || !userStateReady
  if (shouldRedirectToLogin) {
    const url = new URL(request.url)
    const returnUrl = url.pathname + url.search
    // Don't add returnUrl param if already going to root
    if (returnUrl && returnUrl !== '/') {
      return redirect(
        `${ApplicationRoutes.LOGIN}?returnUrl=${encodeURIComponent(returnUrl)}`,
      )
    }
    return redirect(ApplicationRoutes.LOGIN)
  }
  return authState
}

redirectToDesktopHandoffIfNeeded()

// Define the router
export const router = createBrowserRouter([
  {
    path: '/',
    loader: authCheckLoader,
    Component: HomePage,
    errorElement: <ResourceErrorBoundary />,
    children: [
      {
        index: true,
        Component: DefaultHomeRoute,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'calls/*',
        Component: CallsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':callId',
            Component: CallDetailsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'missed-calls/*',
        Component: MissedCallsPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'voicemails/*',
        Component: VoicemailPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':voicemailId',
            Component: VoicemailPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'messaging/*',
        Component: MessagingPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'faxes/*',
        Component: FaxPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':faxId',
            Component: FaxPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'contacts/*',
        Component: ContactsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            path: ':contactId',
            Component: ContactsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'users',
        loader: ({ request }) => {
          const url = new URL(request.url)
          return redirect(`/admin/users${url.search}`)
        },
      },
      {
        path: 'users/:userId',
        loader: ({ request, params }) => {
          const url = new URL(request.url)
          return redirect(`/admin/users/${params.userId}${url.search}`)
        },
      },
      {
        path: 'call-analytics/*',
        Component: CallAnalyticsPage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'web-phone/*',
        Component: WebPhonePage,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'reporting',
        Component: DashboardsLayout,
        errorElement: <ResourceErrorBoundary />,
        children: [
          {
            index: true,
            Component: DashboardIndexRedirect,
          },
          {
            path: 'agent-status',
            Component: AgentStatusPage,
            errorElement: <ResourceErrorBoundary />,
          },
          {
            path: 'event-log',
            Component: EventLogPage,
            errorElement: <ResourceErrorBoundary />,
          },
          {
            path: 'queue-dashboard',
            Component: QueueDashboardPage,
            errorElement: <ResourceErrorBoundary />,
          },
          {
            path: 'inbound-answer-rate',
            Component: InboundAnswerRatePage,
            errorElement: <ResourceErrorBoundary />,
          },
          {
            path: 'number-comparison',
            Component: NumberComparisonPage,
            errorElement: <ResourceErrorBoundary />,
          },
          {
            path: 'extension-comparison',
            Component: ExtensionComparisonPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      {
        path: 'settings/*',
        Component: SettingsPage,
        errorElement: <ResourceErrorBoundary />,
        children: [
          // Backward-compat redirects: admin pages that moved to /admin/*
          {
            path: 'users',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`/admin/users${url.search}`)
            },
          },
          {
            path: 'users/:userId',
            loader: ({ request, params }) => {
              const url = new URL(request.url)
              return redirect(`/admin/users/${params.userId}${url.search}`)
            },
          },
          {
            path: 'user-groups',
            loader: () => redirect('/admin/user-groups'),
          },
          {
            path: 'user-groups/add',
            loader: () => redirect('/admin/user-groups/add'),
          },
          {
            path: 'user-groups/:userGroupId/edit',
            loader: ({ params }) =>
              redirect(`/admin/user-groups/${params.userGroupId}/edit`),
          },
          {
            path: 'messaging-groups/:groupId',
            loader: ({ params }) =>
              redirect(`/admin/messaging-groups/${params.groupId}`),
          },
          {
            path: 'messaging-groups',
            loader: () => redirect('/admin/messaging-groups'),
          },
          {
            path: 'switchboards/:extensionDialPlan',
            loader: ({ params }) =>
              redirect(`/admin/switchboards/${params.extensionDialPlan}`),
          },
          {
            path: 'switchboards',
            loader: () => redirect('/admin/switchboards'),
          },
          {
            path: 'devices',
            loader: () => redirect('/admin/devices'),
          },
          {
            path: 'devices/new',
            loader: () => redirect('/admin/devices/new'),
          },
          {
            path: 'devices/:deviceId',
            loader: ({ params }) =>
              redirect(`/admin/devices/${params.deviceId}`),
          },
          {
            path: 'sound-clips',
            loader: () => redirect('/admin/sound-clips'),
          },
          {
            path: 'sound-clips/new',
            loader: () => redirect('/admin/sound-clips/new'),
          },
          {
            path: 'sound-clips/:soundClipId',
            loader: ({ params }) =>
              redirect(`/admin/sound-clips/${params.soundClipId}`),
          },
          {
            path: 'music-on-hold/:playlistId',
            loader: ({ params }) =>
              redirect(`/admin/music-on-hold/${params.playlistId}`),
          },
          {
            path: 'music-on-hold',
            loader: () => redirect('/admin/music-on-hold'),
          },
          {
            path: 'phone-numbers',
            loader: () => redirect('/admin/phone-numbers'),
          },
          {
            path: 'phone-numbers/:phoneNumberId',
            loader: ({ params }) =>
              redirect(`/admin/phone-numbers/${params.phoneNumberId}`),
          },
          {
            path: 'extensions',
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`/admin/extensions${url.search}`)
            },
          },
          {
            path: 'extensions/:type/:extensionId',
            loader: ({ params }) =>
              redirect(
                `/admin/extensions/${params.type}/${params.extensionId}`,
              ),
          },
          {
            path: 'hotels',
            loader: () => redirect(ApplicationRoutes.HOTELS),
          },
          {
            path: 'hotels/settings',
            loader: () => redirect(ApplicationRoutes.HOTELS_SETTINGS),
          },
          {
            path: 'hotels/wakeup',
            loader: () => redirect(ApplicationRoutes.HOTELS_WAKEUP),
          },
          {
            path: 'hotels/wakeup-history',
            loader: () => redirect(ApplicationRoutes.HOTELS_WAKEUP_HISTORY),
          },
          {
            path: 'pbx-settings',
            loader: () => redirect('/admin/pbx-settings'),
          },
          {
            path: 'schedules',
            loader: () => redirect('/admin/schedules'),
          },
          {
            path: 'system-settings',
            loader: () => redirect('/admin/system-settings'),
          },
          {
            path: 'api-access',
            loader: () => redirect('/admin/api-access'),
          },
          // Non-admin settings routes
          {
            path: ':setting',
            Component: SettingsPage,
            errorElement: <ResourceErrorBoundary />,
          },
        ],
      },
      // Admin panel routes
      {
        path: 'hotels',
        Component: HotelsDashboardRoute,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'hotels/settings',
        Component: HotelsSettingsRoute,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'hotels/wakeup',
        Component: HotelsWakeupRoute,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'hotels/wakeup-history',
        Component: HotelsWakeupHistoryRoute,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'admin/hotels',
        loader: () => redirect(ApplicationRoutes.HOTELS),
      },
      {
        path: 'admin/hotels/settings',
        loader: () => redirect(ApplicationRoutes.HOTELS_SETTINGS),
      },
      {
        path: 'admin/hotels/wakeup',
        loader: () => redirect(ApplicationRoutes.HOTELS_WAKEUP),
      },
      {
        path: 'admin/hotels/wakeup-history',
        loader: () => redirect(ApplicationRoutes.HOTELS_WAKEUP_HISTORY),
      },
      {
        path: 'status',
        Component: StatusPanel,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'status/advanced',
        Component: StatusPanel,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'admin',
        Component: AdminPanel,
        errorElement: <ResourceErrorBoundary />,
      },
      {
        path: 'admin/*',
        Component: AdminPanel,
        errorElement: <ResourceErrorBoundary />,
        children: [
          { path: 'users', Component: AdminPanel },
          { path: 'users/:userId', Component: AdminPanel },
          { path: 'user-groups', Component: AdminPanel },
          { path: 'user-groups/add', Component: AdminPanel },
          { path: 'user-groups/:userGroupId/edit', Component: AdminPanel },
          { path: 'messaging-groups', Component: AdminPanel },
          { path: 'messaging-groups/:groupId', Component: AdminPanel },
          { path: 'switchboards', Component: AdminPanel },
          { path: 'switchboards/:extensionDialPlan', Component: AdminPanel },
          { path: 'devices', Component: AdminPanel },
          { path: 'devices/new', Component: AdminPanel },
          { path: 'devices/:deviceId', Component: AdminPanel },
          { path: 'sound-clips', Component: AdminPanel },
          { path: 'sound-clips/new', Component: AdminPanel },
          { path: 'sound-clips/:soundClipId', Component: AdminPanel },
          { path: 'music-on-hold', Component: AdminPanel },
          { path: 'music-on-hold/:playlistId', Component: AdminPanel },
          { path: 'phone-numbers', Component: AdminPanel },
          { path: 'phone-numbers/:phoneNumberId', Component: AdminPanel },
          { path: 'extensions', Component: AdminPanel },
          { path: 'extensions/:type/:extensionId', Component: AdminPanel },
          { path: 'integrations', Component: AdminPanel },
          { path: 'integrations/:integrationId', Component: AdminPanel },
          { path: 'schedules', Component: AdminPanel },
          { path: 'schedules/:id', Component: AdminPanel },
          { path: 'system-settings', Component: AdminPanel },
          { path: 'pbx-settings', Component: AdminPanel },
          { path: 'api-access', Component: AdminPanel },
        ],
      },
      {
        path: 'schedules/:id?',
        Component: SchedulesRedirect,
      },
      {
        path: 'integrations',
        loader: ({ request }) => {
          const url = new URL(request.url)
          return redirect(`/admin/integrations${url.search}`)
        },
      },
      {
        path: 'integrations/:integrationId',
        loader: ({ request, params }) => {
          const url = new URL(request.url)
          return redirect(
            `/admin/integrations/${params.integrationId}${url.search}`,
          )
        },
      },
    ],
  },
  {
    path: '/login',
    Component: LoginPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    path: '/select-pbx',
    Component: SelectPbxPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    path: ApplicationRoutes.FORGOT_PASSWORD,
    Component: ForgotPasswordPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    path: ApplicationRoutes.RESET_PASSWORD,
    Component: ResetPasswordPage,
    errorElement: <ResourceErrorBoundary />,
  },
  ...(import.meta.env.DEV
    ? [
        {
          path: '/secure-fax/mock',
          Component: SecureFaxMockPage,
          errorElement: <ResourceErrorBoundary />,
        },
      ]
    : []),
  {
    path: '/secure-fax/:token',
    Component: SecureFaxPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    path: '/virtualfax/securefax/:token',
    Component: SecureFaxPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    // Public interstitial (CUS-877); resolves ?to=<key> to a mango:// deep link.
    path: '/open',
    Component: OpenAppPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    // Shown after "Always open in desktop app" hands a fresh tab off; public so
    // it renders before (and instead of) the login redirect.
    path: ApplicationRoutes.OPENED_IN_DESKTOP,
    Component: OpenedInDesktopPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    // Back-compat: already-sent emails link to /desktop-update.
    path: '/desktop-update',
    loader: () => redirect('/open?to=updates'),
  },
  {
    path: '/icons',
    element: (
      <React.Suspense fallback={<LoadingComponent />}>
        <IconsPage />
      </React.Suspense>
    ),
  },
  {
    path: '/popup',
    Component: PopupPage,
    errorElement: <ResourceErrorBoundary />,
  },
  {
    // Reroutes to popup with some mock data for testing
    path: '/popup-dev',
    loader: () => redirect('/popup?dev=true'),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
